// ** React Imports
import { Fragment } from 'react'

// ** Next Imports
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ListItemIcon from '@mui/material/ListItemIcon'
import MuiListItem from '@mui/material/ListItem'

// ** Third Party Imports
import clsx from 'clsx'

// ** Theme Config Import
import themeConfig from 'src/configs/themeConfig'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

// ** Util Imports
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { handleURLQueries } from 'src/@core/layouts/utils'

const ListItem = styled(MuiListItem)(({ theme }) => ({
  width: 'auto',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
  borderRadius: '8px',
  transition: 'transform 0.2s ease',
  '&.top-level-item': {
    '@media (max-width: 1600px)': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      '& .icon-circle': {
        width: '34px !important',
        height: '34px !important',
        minWidth: '34px !important'
      },
      '& .MuiTypography-root': {
        fontSize: '12.5px !important',
        letterSpacing: '0.4px !important'
      }
    }
  },
  '&.enhanced': {
    paddingTop: theme.spacing(3.5),
    paddingBottom: theme.spacing(3.5),
    '&.top-level-item': {
      '@media (max-width: 1600px)': {
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25)
      }
    },
    '&:hover': {
      backgroundColor: '#f0f4f4 !important',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
      transition: 'transform 0.2s ease',
      '& .MuiTypography-root': {
        color: '#12205A !important'
      },
      '& .icon-circle': {
        backgroundColor: 'var(--icon-color) !important',
        transition: 'none',
        '& .MuiListItemIcon-root': {
          color: '#ffffff !important'
        }
      }
    }
  },
  '&.active, &.active:hover': {
    backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08)
  },
  '&.active .MuiTypography-root, &.active .MuiListItemIcon-root': {
    color: theme.palette.primary.main
  },
  '&:focus-visible': {
    outline: 0,
    backgroundColor: theme.palette.action.focus
  }
}))

const HorizontalNavLink = props => {
  // ** Props
  const { item, settings, hasParent } = props

  // ** Hook & Vars
  const router = useRouter()

  const { navSubItemIcon, menuTextTruncate } = themeConfig

  const icon = item.icon ? item.icon : navSubItemIcon

  // Show enhanced styling for main menu items or any submenu items with icons and subtitles
  const shouldShowEnhancedStyling = !hasParent || (item.icon && item.subtitle)

  const Wrapper = !hasParent ? List : Fragment

  const isNavLinkActive = () => {
    if (router.pathname === item.path || handleURLQueries(router, item.path)) {
      return true
    } else {
      return false
    }
  }

  return (
    <CanViewNavLink navLink={item}>
      <Wrapper
        {...(!hasParent ? { component: 'div', sx: { py: settings.skin === 'bordered' ? 2.625 : 2.75, px: 1 } } : {})}
      >
        <ListItem
          component={Link}
          disabled={item.disabled}
          {...(item.disabled && { tabIndex: -1 })}
          className={clsx({
            active: isNavLinkActive(),
            enhanced: shouldShowEnhancedStyling,
            'top-level-item': !hasParent
          })}
          target={item.openInNewTab ? '_blank' : undefined}
          href={item.path === undefined ? '/' : `${item.path}`}
          onClick={e => {
            if (item.path === undefined) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          sx={{
            ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
            ...(!hasParent
              ? {
                borderRadius: '20px',
                '&.active, &.active:hover': {
                  backgroundColor: '#b4b8d5',
                  '&:focus-visible': { backgroundColor: '#b4b8d5' },
                  '& .MuiTypography-root, & .MuiListItemIcon-root': { color: '#12205A' }
                }
              }
              : {
                '&.active, &.active:hover': {
                  '&:focus-visible': {
                    backgroundColor: theme => hexToRGBA(theme.palette.primary.main, 0.24)
                  }
                }
              })
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: shouldShowEnhancedStyling ? 3 : 0,
              ...(!hasParent && {
                '@media (max-width: 1600px)': {
                  gap: shouldShowEnhancedStyling ? 1 : 0
                }
              })
            }}
          >
            {shouldShowEnhancedStyling && (
              <Box
                className='icon-circle'
                sx={{
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%',
                  backgroundColor: item.iconBgColor || '#e8eef9',
                  transition: 'all 0.2s ease',
                  '--icon-color': item.iconColor || '#5b78b5'
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'auto',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: `${item.iconColor || '#5b78b5'} !important`
                  }}
                >
                  <UserIcon icon={icon} fontSize='1.5rem' />
                </ListItemIcon>
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                ...(menuTextTruncate && { overflow: 'hidden' })
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  {...(menuTextTruncate && { noWrap: true })}
                  sx={{
                    textTransform: hasParent ? 'capitalize' : 'uppercase',
                    fontSize: '14px',
                    color: hasParent ? 'text.primary' : 'customColors.headerText',
                    fontWeight: hasParent ? 600 : 'normal',
                    letterSpacing: '1px'
                  }}
                >
                  <Translations text={item.title} />
                </Typography>
                {item.badgeContent ? (
                  <Chip
                    size='small'
                    label={item.badgeContent}
                    color={item.badgeColor || 'primary'}
                    sx={{ '& .MuiChip-label': { px: 2, lineHeight: 1.385, textTransform: 'capitalize' } }}
                  />
                ) : null}
              </Box>
              {shouldShowEnhancedStyling && item.subtitle && (
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    mt: 1,
                    lineHeight: 1.3,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                  }}
                >
                  {item.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </ListItem>
      </Wrapper>
    </CanViewNavLink>
  )
}

export default HorizontalNavLink
